import React, { useState, useEffect, useRef, useCallback } from "react";
import { ScrollText, X, Image as ImageIcon, Video, FileText } from "lucide-react";
import { useTheme } from "@context/ThemeContext";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import GenerationLogItem from "./GenerationLogItem";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";

const PAGE_SIZE = 20;
const POLL_INTERVAL = 10000;

const GenerationLogPanel = ({ isOpen, onClose, projectId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null); // null=全部, "IMAGE", "VIDEO", "TEXT"
  const scrollRef = useRef(null);
  const pollTimerRef = useRef(null);

  // 加载数据
  const fetchLogs = useCallback(async (offset = 0, append = false, { silent = false } = {}) => {
    if (!projectId) return;
    if (!silent) setLoading(true);
    try {
      let url = `${API_HOST}/api/generation-log/list?projectId=${projectId}&offset=${offset}&limit=${PAGE_SIZE}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      const data = await authenticatedFetch(url);
      const newLogs = data.logs || [];
      if (append) {
        setLogs((prev) => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }
      setTotal(data.total || 0);
    } catch (e) {
      console.warn("[GenLog] fetch failed:", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId, typeFilter]);

  // 打开时加载 + 启动轮询
  useEffect(() => {
    if (isOpen && projectId) {
      fetchLogs(0, false);
      pollTimerRef.current = setInterval(() => fetchLogs(0, false, { silent: true }), POLL_INTERVAL);
    }
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchLogs, isOpen, projectId, typeFilter]);

  // 滚动加载更多
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      if (logs.length < total) {
        fetchLogs(logs.length, true);
      }
    }
  }, [logs.length, total, loading, fetchLogs]);

  // 切换筛选
  const handleFilterChange = (type) => {
    setTypeFilter(type === typeFilter ? null : type);
    setLogs([]);
  };

  const drawer = useDelayedUnmount(isOpen, 150);

  if (!drawer.mounted) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <div
        className={`fixed inset-0 z-40 ${drawer.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`}
        style={{ backgroundColor: "var(--overlay-light)" }}
        onClick={onClose}
      />

      {/* 面板 */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col ${drawer.exiting ? "animate-out fade-out slide-out-to-right-4 duration-150" : "animate-in fade-in slide-in-from-right-4 duration-200"}`}
        style={{
          width: 480,
          maxWidth: "90vw",
          backgroundColor: "var(--color-bg-secondary)",
          borderLeft: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-modal, -8px 0 32px rgba(0,0,0,0.3))",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: "1px solid var(--color-border-primary)" }}
        >
          <div className="flex items-center gap-2">
            <ScrollText size={16} style={{ color: theme.colors.accentPrimary }} />
            <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
              生成日志
            </span>
            <span className="text-2xs px-1.5 py-0.5 rounded-full" style={{
              backgroundColor: "var(--color-bg-tertiary)",
              color: "var(--color-text-muted)",
            }}>
              {total}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
            style={{ color: "var(--color-text-muted)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-bg-tertiary)";
              e.currentTarget.style.color = "var(--color-text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--color-text-muted)";
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* 筛选栏 */}
        <div className="flex items-center gap-2 px-4 py-2 shrink-0" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <FilterTab label="全部" active={typeFilter === null} onClick={() => handleFilterChange(null)} theme={theme} />
          <FilterTab label="图片" icon={<ImageIcon size={12} />} active={typeFilter === "IMAGE"} onClick={() => handleFilterChange("IMAGE")} theme={theme} />
          <FilterTab label="视频" icon={<Video size={12} />} active={typeFilter === "VIDEO"} onClick={() => handleFilterChange("VIDEO")} theme={theme} />
          <FilterTab label="文本" icon={<FileText size={12} />} active={typeFilter === "TEXT"} onClick={() => handleFilterChange("TEXT")} theme={theme} />
        </div>

        {/* 列表 */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2"
          onScroll={handleScroll}
        >
          {!projectId ? (
            <EmptyState text="请先进入一个项目" />
          ) : logs.length === 0 && !loading ? (
            <EmptyState text="暂无生成记录" />
          ) : (
            <>
              {logs.map((log) => (
                <GenerationLogItem key={log.id} log={log} />
              ))}
              {loading && (
                <div className="text-center py-4 text-xs" style={{ color: "var(--color-text-muted)" }}>
                  加载中...
                </div>
              )}
              {logs.length >= total && logs.length > 0 && !loading && (
                <div className="text-center py-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                  已加载全部
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const FilterTab = ({ label, icon, active, onClick, theme }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium transition-all duration-200 cursor-pointer"
    style={{
      backgroundColor: active ? `${theme.colors.accentPrimary}20` : "transparent",
      color: active ? theme.colors.accentPrimary : "var(--color-text-muted)",
      border: `1px solid ${active ? theme.colors.accentPrimary : "transparent"}`,
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.backgroundColor = "var(--color-bg-tertiary)";
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.backgroundColor = "transparent";
    }}
  >
    {icon}
    {label}
  </button>
);

const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-3">
    <ScrollText size={32} style={{ color: "var(--color-text-muted)", opacity: 0.3 }} />
    <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{text}</span>
  </div>
);

export default GenerationLogPanel;
